import { emptyDocument, type DrawingDocument } from '../model';
import { buildDocument } from './build';
import { History } from './history';
import { sameLayers, type ScriptLayers } from './layers';
import { sameParams, type ScriptParams } from './params';
import type { ScriptError, ScriptRuntime } from './runtime';
import { DEFAULT_SETTINGS, displayOf, sameSettings, type ScriptSettings } from './settings';

type Listener = () => void;

/** Everything saved for a drawing, which undo and redo step through as a whole. */
export interface ScriptState {
  readonly name: string;
  readonly source: string;
  readonly params: ScriptParams;
  readonly layers: ScriptLayers;
  readonly settings: ScriptSettings;
}

/**
 * A drawing whose source of truth is its script. Running the script replaces the document;
 * a failed run keeps the last good document and records the error. Every change to what is
 * saved is one step in a single undo history, wherever it was made.
 */
export class ScriptDrawing {
  private runtime: ScriptRuntime | null = null;
  private readonly listeners = new Set<Listener>();

  name = '';
  source = '';
  /** The drawing's params, kept beside the script; the script only reads them. */
  params: ScriptParams = [];
  /** The drawing's layers, kept beside the script; the script only puts shapes on them. */
  layers: ScriptLayers = [];
  /** The drawing's settings, kept beside the script, such as how far views stand clear. */
  settings: ScriptSettings = DEFAULT_SETTINGS;
  document: DrawingDocument = emptyDocument();
  error: ScriptError | null = null;
  /** Whether the current script has run, so a viewer knows the document is its output. */
  ran = false;
  /**
   * Each entity's script line and the top-level line that led to it, from a second run made
   * on the first `lineOf` or `idsAt` after a run.
   */
  private lines: Map<string, { line: number | null; call: number | null }> | null = null;
  private readonly history = new History<ScriptState>();

  get state(): ScriptState {
    const { name, source, params, layers, settings } = this;
    return { name, source, params, layers, settings };
  }

  get canUndo(): boolean {
    return this.history.canUndo;
  }

  get canRedo(): boolean {
    return this.history.canRedo;
  }

  /** Scripts run once the sandbox has loaded; until then a change only records the text. */
  attach(runtime: ScriptRuntime): void {
    this.runtime = runtime;
    this.run();
  }

  load(
    name: string,
    source: string,
    params: ScriptParams = [],
    layers: ScriptLayers = [],
    settings: ScriptSettings = DEFAULT_SETTINGS,
  ): void {
    this.name = name;
    this.source = source;
    this.params = params;
    this.layers = layers;
    this.settings = settings;
    this.document = emptyDocument(name);
    this.error = null;
    this.ran = false;
    this.history.clear();
    this.run();
  }

  setSource(source: string): void {
    this.change({ source });
  }

  /** Takes a version saved elsewhere as one step, running the script only if anything changed. */
  replace(state: ScriptState): void {
    this.change(state);
  }

  /** `field` names what a keystroke-by-keystroke edit changes, so quick ones are one step. */
  setParams(params: ScriptParams, field?: string): void {
    this.change({ params }, field);
  }

  setLayers(layers: ScriptLayers, field?: string): void {
    this.change({ layers }, field);
  }

  setSettings(settings: ScriptSettings): void {
    this.change({ settings });
  }

  rename(name: string): void {
    this.change({ name });
  }

  undo(): void {
    const state = this.history.undo(this.state);
    if (state) this.apply(state);
  }

  redo(): void {
    const state = this.history.redo(this.state);
    if (state) this.apply(state);
  }

  private change(change: Partial<ScriptState>, field?: string): void {
    const next = { ...this.state, ...change };
    if (sameState(next, this.state)) return;
    this.history.record(this.state, field);
    this.apply(next);
  }

  private apply(state: ScriptState): void {
    const scriptChanged = !sameState({ ...state, name: this.name }, this.state);
    ({
      name: this.name,
      source: this.source,
      params: this.params,
      layers: this.layers,
      settings: this.settings,
    } = state);
    if (scriptChanged) {
      this.run();
      return;
    }
    this.document = { ...this.document, name: this.name };
    this.notify();
  }

  /**
   * The script line that made an entity. Lines cost a run as long again, so they come from
   * a run of their own, on demand; the same script makes the same ids in the same order.
   */
  lineOf(id: string): number | null {
    return this.sourceLines().get(id)?.line ?? null;
  }

  /** The entities a script line made, or led to as a top-level call such as a helper's. */
  idsAt(line: number): string[] {
    const ids: string[] = [];
    for (const [id, source] of this.sourceLines())
      if (source.line === line || source.call === line) ids.push(id);
    return ids;
  }

  private sourceLines(): ReadonlyMap<string, { line: number | null; call: number | null }> {
    if (!this.lines) {
      this.lines = new Map();
      const result = this.runtime?.run(this.source, this.params, this.layers, {
        lines: true,
        settings: this.settings,
      });
      if (result?.ok) {
        const { lines = [], calls = [] } = result.output;
        this.document.rootIds.forEach((each, index) => {
          this.lines!.set(each, { line: lines[index] ?? null, call: calls[index] ?? null });
        });
      }
    }
    return this.lines;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private run(): void {
    if (!this.runtime) {
      this.notify();
      return;
    }
    const result = this.runtime.run(this.source, this.params, this.layers, {
      settings: this.settings,
    });
    this.ran = true;
    this.lines = null;
    if (result.ok) {
      try {
        this.document = buildDocument(
          result.output,
          this.name,
          this.layers,
          displayOf(this.settings),
        );
        this.error = null;
      } catch (error) {
        this.error = {
          message: error instanceof Error ? error.message : String(error),
          line: null,
          column: null,
        };
      }
    } else {
      this.error = result.error;
    }
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

const sameState = (a: ScriptState, b: ScriptState) =>
  a.name === b.name &&
  a.source === b.source &&
  sameParams(a.params, b.params) &&
  sameLayers(a.layers, b.layers) &&
  sameSettings(a.settings, b.settings);
